'use client'

import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { useSearchFilterStore } from '@/entities/map'
import { MobilePeriodPicker, useSearchExecution } from '@/features/room-search'
import {
  cn,
  isNilOrEmptyString,
  Slot,
  type StringDateRange,
  useFormatDate,
} from '@/shared/lib'
import { Icon, ScreenOverlay, TextField } from '@/shared/ui'
import { MobileSearchOverlay } from '@/widgets/map-search-overlay'

export const MobileSearchBar = () => {
  const t = useTranslations('map')

  const { formatDate } = useFormatDate()

  const { startDate, endDate } = useSearchFilterStore(state => state.filters)
  const committedKeyword = useSearchFilterStore(state => state.keyword)
  const { commitSearch } = useSearchExecution()

  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [isPeriodPickerOpen, setIsPeriodPickerOpen] = useState(false)

  const dateInputValue = useMemo(() => {
    if (isNilOrEmptyString(startDate) || isNilOrEmptyString(endDate)) return ''

    return `${formatDate({ date: startDate, format: 'MM.dd' })} - ${formatDate({ date: endDate, format: 'MM.dd' })}`
  }, [startDate, endDate, formatDate])

  const openOverlay = () => {
    setIsOverlayOpen(true)
  }

  const closeOverlay = () => {
    setIsOverlayOpen(false)
  }

  const openBarPeriodPicker = () => {
    setIsPeriodPickerOpen(true)
  }

  const handleBarDateChange = (dateRange?: StringDateRange) => {
    commitSearch({ keyword: committedKeyword, dateRange })
  }

  return (
    <ScreenOverlay.Root open={isOverlayOpen}>
      <div className="flex h-[57px] gap-4 border-b border-b-gray-100 bg-white px-4 py-[3px]">
        <ScreenOverlay.Trigger asChild onClick={openOverlay}>
          <TextField
            value={committedKeyword}
            readOnly
            type="text"
            placeholder={t('search.location.mobile_placeholder')}
            className="w-3/5 border-none bg-[rgba(0,0,0,0)] text-[15px]! leading-[22px]! *:placeholder:text-gray-400"
            padding={{ x: 0, y: 13 }}
          >
            <Slot name="left">
              <Icon
                name="search"
                size={24}
                className="text-black"
                strokeWidth={1.5}
              />
            </Slot>
          </TextField>
        </ScreenOverlay.Trigger>

        <MobilePeriodPicker
          open={isPeriodPickerOpen}
          onOpenChange={setIsPeriodPickerOpen}
          onChange={handleBarDateChange}
          className="w-2/5 shrink-0"
        >
          <TextField
            value={dateInputValue}
            readOnly
            placeholder={t('search.date.mobile_placeholder')}
            onClick={openBarPeriodPicker}
            tabIndex={-1}
            type="text"
            className={cn(
              'cursor-pointer border-none bg-[rgba(0,0,0,0)] text-[15px]! leading-[22px]!',
              '*:placeholder:text-gray-400 *:read-only:cursor-pointer',
              'w-full focus-visible:outline-none',
            )}
            padding={{ x: 0, y: 13 }}
          >
            <Slot name="left">
              <Icon
                name="calendar"
                className="text-black"
                size="24"
                strokeWidth={1.5}
              />
            </Slot>
          </TextField>
        </MobilePeriodPicker>
      </div>

      <MobileSearchOverlay isOpen={isOverlayOpen} onClose={closeOverlay} />
    </ScreenOverlay.Root>
  )
}
